import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import TopBar from 'TailwindcssComponents/TopBar';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import { Icon } from "@iconify/react";

export default function EditVenue() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [venueFeatures, setVenueFeatures] = useState([]);
    const [images, setImages] = useState([]);
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [venueCities, setVenueCities] = useState([]);
    const [initialSlug, setInitialSlug] = useState('');
    const [initialName, setInitialName] = useState('');
    const prevSlugRef = useRef('');
    const debounceTimer = useRef(null);

    const fetchVenueData = async () => {
        try {
            const response = await axios.get(`${apiUrl}event-service/event-venue/${id}`);
            if (response.data.success && response.data.data) {
                const venue = response.data?.data;
                editVenue.setValues({
                    name: venue.name,
                    slug: venue.slug,
                    location: venue.location,
                    cityId: venue.cityId,
                    address: venue.address,
                    isSeat: venue.isSeat,
                    ownerName: venue.ownerName,
                    ownerEmail: venue.ownerEmail,
                    ownerPhone: venue.ownerPhone,
                    lat: venue.coordinate?.lat || '',
                    lng: venue.coordinate?.lng || '',
                });
                setSelectedFeatures(venue.features.map((feature) => feature.id));
                setExistingImages(venue.images || []);
                setImages(venue.images || []);
                setInitialName(venue.name);
                setInitialSlug(venue.slug);
            }
        } catch (error) {
            console.error('Error fetching venue data:', error.message);
        }
    };

    const fetchFeatures = async () => {
        try {
            const response = await axios.get(`${apiUrl}event-service/venue-feature`);
            if (response.data?.success) {
                setVenueFeatures(response.data.data);
            } else throw new Error(response.data?.message || 'Error in fetching features');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    const fetchVenueCities = async () => {
        try {
            const response = await axios.get(`${apiUrl}event-service/city`);
            if (response.data?.success) {
                setVenueCities(response.data.data);
            } else throw new Error(response.data?.message || 'Error in fetching cities');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    useEffect(() => {
        fetchVenueData();
        fetchFeatures();
        fetchVenueCities();
    }, [id]);

    const editVenue = useFormik({
        initialValues: {
            name: '',
            slug: '',
            location: '',
            cityId: '',
            address: '',
            ownerName: '',
            ownerPhone: '',
            ownerEmail: '',
            isSeat: true,
            features: [],
            lat: '',
            lng: '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
            slug: Yup.string().required("Required"),
            cityId: Yup.string().required('Required'),
            address: Yup.string().required('Required'),
            ownerName: Yup.string().required('Required'),
            ownerPhone: Yup.string()
                .required("Required")
                .matches(/^\+94\d{9}$/, "Phone number must start with +94 and contain 9 digits after it")
                .length(12, "Phone number must be exactly 12 characters"),
            ownerEmail: Yup.string()
                .required('Required')
                .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email')
                .email('Enter a valid email'),
            location: Yup.string()
                .matches(
                    /^https:\/\/www\.google\.com\/maps\/embed\?pb=.*/i,
                    'Please enter a valid embedded Google Maps URL'
                )
                .required('Location URL is required'),
            lat: Yup.number()
                .typeError('Latitude must be a decimal number')
                .required('Latitude is required')
                .min(-90, 'Latitude must be at least -90')
                .max(90, 'Latitude must be at most 90'),
            lng: Yup.number()
                .typeError('Longitude must be a decimal number')
                .required('Longitude is required')
                .min(-180, 'Longitude must be at least -180')
                .max(180, 'Longitude must be at most 180'),
        }),
        onSubmit: async (values, { resetForm }) => {
            setIsLoading(true);
            try {
                // await validateSlug(values.slug, editVenue.setFieldError, editVenue.setFieldTouched);
                const formData = new FormData();
                formData.append("name", values.name);
                formData.append("location", values.location);
                formData.append("cityId", values.cityId);
                formData.append("address", values.address);
                formData.append("ownerName", values.ownerName);
                formData.append("ownerPhone", values.ownerPhone);
                formData.append("ownerEmail", values.ownerEmail);
                formData.append("isSeat", values.isSeat);
                formData.append("features", JSON.stringify(selectedFeatures));
                images.forEach((image) => {
                    if (image.file) {
                        formData.append("images", image.file);
                    }
                });
                formData.append("coordinate", JSON.stringify({
                    lat: parseFloat(values.lat),
                    lng: parseFloat(values.lng)
                }));
                if (existingImages) {
                    formData.append("updateImages", JSON.stringify(existingImages));
                }

                const response = await axios.put(`${apiUrl}event-service/event-venue/${id}`, formData, {
                    headers: {
                        'Authorization': `Bearer ${bearerToken}`,
                        'Content-Type': 'multipart/form-data',
                        'accept': 'application/json',
                    },
                });

                if (response.data?.success) {
                    resetForm();
                    setExistingImages([]);
                    setImages([]);
                    setSelectedFeatures([]);
                    setTimeout(() => {
                        navigate('/venues');
                    }, 500);
                    toast.success(response.data?.message || 'Venue has been successfully updated!');
                } else {
                    throw new Error('Failed to update venue');
                }
            } catch (error) {
                console.error('Error submitting form:', error.response?.data?.message || error.message);
                toast.error(error.response?.data?.message || error.message);
            } finally {
                setIsLoading(false);
            }
        },
    });

    const handleFeaturesChange = (featureId) => {
        setSelectedFeatures((prevSelected) => {
            const updatedFeatures = prevSelected.includes(featureId)
                ? prevSelected.filter((id) => id !== featureId)
                : [...prevSelected, featureId];
            editVenue.setFieldValue("permissions", updatedFeatures);
            return updatedFeatures;
        });
    };

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => ({
            file,
            secure_url: URL.createObjectURL(file),
        }));
        setImages(prev => [...prev, ...newImages]);
    };

    const handleRemoveImage = (index) => {
        const updatedImages = images.filter((_, i) => i !== index);
        setImages(updatedImages);
        setExistingImages(existingImages.filter((_, i) => i !== index));
    };

    const validateSlug = async (slug, setFieldError, setFieldTouched) => {
        if (!slug || (slug === initialSlug)) return;
        setFieldTouched("slug", true);
        try {
            const response = await axios.get(`${apiUrl}event-service/event-venue/check-slug/${slug}`);
            if (!response.data.success) {
                setFieldError("slug", response.data.message || "Slug is not available");
            }
        } catch (error) {
            if (error.response?.status === 400) {
                setFieldError("slug", error.response.data.message || "Slug already taken");
            } else {
                setFieldError("slug", "Error validating slug. Please try again.");
            }
        }
    };

    useEffect(() => {
        const name = editVenue.values.name.trim();
        const currentSlug = editVenue.values.slug.trim();

        // Generate slug from name only if slug hasn't been manually edited
        if (name && name !== initialName && currentSlug === initialSlug) {
            const generatedSlug = name
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '');

            if (generatedSlug !== currentSlug) {
                editVenue.setFieldValue('slug', generatedSlug);
                return;
            }
        }

        // Debounced validation when slug changes
        if (currentSlug && currentSlug !== prevSlugRef.current && currentSlug !== initialSlug) {
            prevSlugRef.current = currentSlug;

            if (debounceTimer.current) clearTimeout(debounceTimer.current);

            debounceTimer.current = setTimeout(() => {
                validateSlug(currentSlug, editVenue.setFieldError, editVenue.setFieldTouched);
            }, 300);
        }
    }, [editVenue.values.name, editVenue.values.slug]);

    return (
        <TailwindLayout>
            <TopBar />
            <div className="max-w-7xl mx-auto p-12 sm:p-12 lg:p-12 mb-8">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">

                    <form onSubmit={editVenue.handleSubmit} className="p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Edit Venue</h2>

                        <div className="grid md:grid-cols-2 gap-6 border-t pt-6">

                            {/* Venue Info Column */}
                            <div>
                                <h3 className="text-lg font-medium mb-4">Venue Info</h3>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="name">
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Name"
                                        value={editVenue.values.name}
                                        onChange={editVenue.handleChange}
                                        onBlur={editVenue.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {editVenue.touched.name && editVenue.errors.name && <div className="text-red-500 text-sm">{editVenue.errors.name}</div>}
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="name">
                                        Unique Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="slug"
                                        placeholder="eg. the-grand-Hall"
                                        value={editVenue.values.slug}
                                        onChange={editVenue.handleChange}
                                        onBlur={() => {
                                            const currentSlug = editVenue.values.slug.trim();
                                            if (currentSlug && currentSlug !== initialSlug) {
                                                validateSlug(currentSlug, editVenue.setFieldError, editVenue.setFieldTouched);
                                            }
                                        }}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {editVenue.touched.slug && editVenue.errors.slug && <div className="text-red-500 text-sm">{editVenue.errors.slug}</div>}
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="location">
                                        Location <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="location"
                                        placeholder="eg. https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.1234567890123!2d-122.4194156846814!3d37.7749292797598!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085808c8c8c8c8c%3A0x8c8c8c8c8c8c8c8c%2C+San+Francisco%2C+CA+94103%2C+USA!5e0!3m2!1sen!2sus!4v1234567890123"
                                        value={editVenue.values.location}
                                        onChange={editVenue.handleChange}
                                        onBlur={editVenue.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {editVenue.touched.location && editVenue.errors.location && <div className="text-red-500 text-sm">{editVenue.errors.location}</div>}
                                </div>
                                <div className='mb-4'>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Latitude <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="lat"
                                                placeholder="eg. 37.7749"
                                                value={editVenue.values.lat}
                                                onChange={editVenue.handleChange}
                                                onBlur={editVenue.handleBlur}
                                                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                            />
                                            {editVenue.touched.lat && editVenue.errors.lat && <div className="text-red-500 text-sm">{editVenue.errors.lat}</div>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Longitude <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="lng"
                                                placeholder="eg. 122.4194"
                                                value={editVenue.values.lng}
                                                onChange={editVenue.handleChange}
                                                onBlur={editVenue.handleBlur}
                                                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                            />
                                            {editVenue.touched.lng && editVenue.errors.lng && <div className="text-red-500 text-sm">{editVenue.errors.lng}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="city">
                                        City <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="cityId"
                                        value={editVenue.values.cityId}
                                        onChange={editVenue.handleChange}
                                        onBlur={editVenue.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    >
                                        <option value="" disabled>
                                            Select City
                                        </option>
                                        {venueCities.map((city) => (
                                            <option key={city.id} value={city.id}>
                                                {city.name}
                                            </option>
                                        ))}
                                    </select>
                                    {editVenue.touched.cityId && editVenue.errors.cityId && <div className="text-red-500 text-sm">{editVenue.errors.cityId}</div>}
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="address">
                                        Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="address"
                                        placeholder="Address"
                                        value={editVenue.values.address}
                                        onChange={editVenue.handleChange}
                                        onBlur={editVenue.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {editVenue.touched.address && editVenue.errors.address && <div className="text-red-500 text-sm">{editVenue.errors.address}</div>}
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="featureId">
                                        Venue Features
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                        {venueFeatures.length === 0 && <div className="text-gray-500 text-sm">No features available</div>}
                                        {venueFeatures.map((feature) => (
                                            <label key={feature.id} className="flex items-center space-x-1 p-0.5 hover:bg-gray-50 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedFeatures && selectedFeatures.includes(feature.id)}
                                                    onChange={() => handleFeaturesChange(feature.id)}
                                                    className="h-3 w-3"
                                                />
                                                <span className="text-xs">{feature.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Venue Owner Info Column */}
                            <div>
                                <h3 className="text-lg font-medium mb-4">Venue Owner Info</h3>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="ownerName">
                                        Owner Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="ownerName"
                                        placeholder="Owner Name"
                                        value={editVenue.values.ownerName}
                                        onChange={editVenue.handleChange}
                                        onBlur={editVenue.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {editVenue.touched.ownerName && editVenue.errors.ownerName && <div className="text-red-500 text-sm">{editVenue.errors.ownerName}</div>}
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="ownerPhone">
                                        Owner Phone <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="ownerPhone"
                                        placeholder="Owner Phone Number"
                                        value={editVenue.values.ownerPhone}
                                        onChange={editVenue.handleChange}
                                        onBlur={editVenue.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {editVenue.touched.ownerPhone && editVenue.errors.ownerPhone && <div className="text-red-500 text-sm">{editVenue.errors.ownerPhone}</div>}
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2" htmlFor="ownerEmail">
                                        Owner Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="ownerEmail"
                                        placeholder="Owner Email"
                                        value={editVenue.values.ownerEmail}
                                        onChange={editVenue.handleChange}
                                        onBlur={editVenue.handleBlur}
                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                    />
                                    {editVenue.touched.ownerEmail && editVenue.errors.ownerEmail && <div className="text-red-500 text-sm">{editVenue.errors.ownerEmail}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Image Upload Section */}
                        <div className="mt-6">
                            <h3 className="text-lg font-medium mb-4">Venue Images <span className="text-gray-500 text-xs">(Recommended size: below 1mb, JPG/JPEG/PNG format)</span></h3>
                            <div className="flex items-center">
                                <label className="flex items-center text-sm font-medium px-4 py-2 text-blue-500 bg-blue-50 hover:bg-blue-200 rounded-lg cursor-pointer">
                                    {/* <ImageIcon size={20} className="mr-2" /> */}
                                    Upload Images
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                    />
                                </label>
                            </div>

                            {/* Image Preview */}
                            <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-4">
                                {images.map((image, index) => (
                                    <div key={index} className="relative">
                                        <img
                                            src={image.secure_url}
                                            alt={`Venue ${index + 1}`}
                                            className="w-full h-32 object-cover rounded-md"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveImage(index)}
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                        >
                                            <Icon icon="carbon:close-outline" className="sm" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="mt-6 flex items-center">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                            >
                                {isLoading ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <FooterTailwind />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </TailwindLayout>
    )
}